import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EnhancedThoughtEntryForm } from '@/components/mental/EnhancedThoughtEntryForm';
import { ThoughtData } from '@/components/mental/ThoughtEntryForm';
import { CognitiveDistortionsPieChart } from '@/components/mental/CognitiveDistortionsPieChart';
import { DistortionProgressChart } from '@/components/mental/DistortionProgressChart';
import { GuidedReframingExercise } from '@/components/mental/GuidedReframingExercise';
import { useThoughtEntries } from '@/hooks/useThoughtEntries';
import { COGNITIVE_DISTORTIONS } from '@/constants/cognitiveDistortions';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, PieChart, List, BookOpen, MessageCircle, 
  Calendar, TrendingUp, Sparkles, Clock, ChevronDown,
  Lightbulb, Target, Heart, LineChart, Scale, Wrench
} from 'lucide-react';
import { format } from 'date-fns';

export default function ThoughtTracker() {
  useNavigationHistory();
  const navigate = useNavigate();
  const { 
    entries, 
    isLoading, 
    saveEntry, 
    distortionCounts, 
    totalThoughts 
  } = useThoughtEntries(60); // Extended to 60 days for better progress tracking

  const [activeTab, setActiveTab] = useState('log');
  const [expandedDistortion, setExpandedDistortion] = useState<string | null>(null);
  const [showReframingExercise, setShowReframingExercise] = useState(false);

  const handleSaveThought = async (data: ThoughtData) => {
    await saveEntry({
      thought_content: data.thought_content,
      situation: data.situation,
      emotion: data.emotion,
      emotion_intensity: data.emotion_intensity,
      cognitive_distortions: data.cognitive_distortions,
      alternative_thought: data.alternative_thought
    });
  };

  const handleChallengeThought = (thought: string, distortions: string[]) => {
    // Navigate to therapy with CBT mode and context
    const distortionNames = distortions
      .map(id => COGNITIVE_DISTORTIONS.find(d => d.id === id)?.name)
      .filter(Boolean)
      .join(', ');
    
    const context = `I'd like help challenging this thought: "${thought}"${
      distortionNames ? `. I've identified these thinking patterns: ${distortionNames}` : ''
    }`;
    
    navigate('/therapy', { 
      state: { 
        mode: 'cbt',
        initialMessage: context,
        skipModeChooser: true
      } 
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Thought Tracker" />

      <div className="container mx-auto p-4 max-w-5xl">
        {/* Hero Section */}
        <Card className="mb-6 bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start gap-4">
              <div className="p-4 rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
                <Brain className="w-10 h-10 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">CBT Thought Tracker</h1>
                <p className="text-muted-foreground mb-4">
                  Your thoughts shape your reality. By noticing patterns and gently challenging unhelpful thinking, 
                  you can reshape how you experience life. This isn't about being positive all the time - it's about being accurate.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>{totalThoughts} thought{totalThoughts !== 1 ? 's' : ''} logged</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-accent" />
                    <span>Last 60 days</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex w-full overflow-x-auto scrollbar-hide gap-1 p-1 h-auto justify-start sm:justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="log" className="flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  <span className="hidden sm:inline">Log</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>Log a new thought</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="patterns" className="flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  <span className="hidden sm:inline">Patterns</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>View your cognitive patterns</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="progress" className="flex items-center gap-2">
                  <LineChart className="w-4 h-4" />
                  <span className="hidden sm:inline">Progress</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>Track your improvement over time</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="history" className="flex items-center gap-2">
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">History</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>View thought history</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="learn" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">Learn</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>Learn about cognitive distortions</TooltipContent>
            </Tooltip>
          </TabsList>

          {/* Log Tab */}
          <TabsContent value="log" className="space-y-6">
            {showReframingExercise ? (
              <GuidedReframingExercise 
                onComplete={(data) => {
                  setShowReframingExercise(false);
                }}
                onCancel={() => setShowReframingExercise(false)}
              />
            ) : (
              <EnhancedThoughtEntryForm 
                onSave={handleSaveThought}
                onChallenge={handleChallengeThought}
                onStartReframe={() => setShowReframingExercise(true)}
              />
            )}
            
            {/* Quick Stats */}
            {totalThoughts > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-chart-1/10 to-transparent">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-chart-1">{totalThoughts}</div>
                    <p className="text-sm text-muted-foreground">Thoughts logged</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-chart-2/10 to-transparent">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-chart-2">{distortionCounts.length}</div>
                    <p className="text-sm text-muted-foreground">Pattern types identified</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-chart-3/10 to-transparent">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-chart-3">
                      {entries.filter(e => e.alternative_thought).length}
                    </div>
                    <p className="text-sm text-muted-foreground">Thoughts reframed</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Patterns Tab */}
          <TabsContent value="patterns" className="space-y-6">
            <CognitiveDistortionsPieChart 
              distortionCounts={distortionCounts}
              totalThoughts={totalThoughts}
            />
            
            {distortionCounts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Top Patterns</CardTitle>
                  <CardDescription>
                    Understanding your common patterns helps you challenge them more effectively. 
                    These patterns aren't flaws - they're habits your mind developed, often to protect you.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {distortionCounts
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 3)
                    .map(({ distortionId, count }) => {
                      const distortion = COGNITIVE_DISTORTIONS.find(d => d.id === distortionId);
                      if (!distortion) return null;
                      return (
                        <div key={distortionId} className="p-4 rounded-lg bg-muted/50 border">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{distortion.name}</h4>
                            <Badge variant="secondary">{count} time{count !== 1 ? 's' : ''}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{distortion.description}</p>
                          <p className="text-sm text-primary italic">Challenge: {distortion.challenge}</p>
                        </div>
                      );
                    })}
                </CardContent>
              </Card>
            )}

            {/* Premium Upgrade Prompt */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full bg-amber-500/20">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-700 dark:text-amber-400">
                      Unlock Deeper Insights
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      With Premium Therappi, your cognitive patterns are connected to your life timeline, 
                      helping you understand where these thinking habits might have originated and how 
                      they connect to your personal experiences.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
                      onClick={() => navigate('/pricing')}
                    >
                      Learn About Premium
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-6">
            <Card className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-green-500/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Your Progress Journey
                </CardTitle>
                <CardDescription>
                  Change takes time and consistency. This isn't about perfection - it's about awareness. 
                  Every thought you log is practice, and practice creates new neural pathways.
                </CardDescription>
              </CardHeader>
            </Card>

            <DistortionProgressChart entries={entries} />

            {/* Encouragement Card */}
            <Card className="border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Remember</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Progress isn't linear. Stress, lack of sleep, hormonal changes, and life events all affect 
                      how our minds process information. A "bad week" on the chart isn't failure - it's information 
                      about what you're going through. Be gentle with yourself.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            {isLoading ? (
              <Card className="p-8">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </Card>
            ) : entries.length === 0 ? (
              <Card className="p-8 text-center">
                <Brain className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No thoughts logged yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('log')}
                >
                  Log your first thought
                </Button>
              </Card>
            ) : (
              entries.map(entry => (
                <Card key={entry.id} className="hover:border-primary/30 transition-colors">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {format(new Date(entry.created_at), 'dd MMM yyyy, HH:mm')}
                      </div>
                      {entry.emotion && (
                        <Badge variant="outline">
                          {entry.emotion} {entry.emotion_intensity && `(${entry.emotion_intensity}/10)`}
                        </Badge>
                      )}
                    </div>
                    
                    {entry.situation && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Situation:</strong> {entry.situation}
                      </p>
                    )}
                    
                    <p className="mb-3">{entry.thought_content}</p>
                    
                    {entry.cognitive_distortions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {entry.cognitive_distortions.map(id => {
                          const d = COGNITIVE_DISTORTIONS.find(cd => cd.id === id);
                          return d ? (
                            <Badge key={id} variant="secondary" className="text-xs">
                              {d.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                    
                    {entry.alternative_thought && (
                      <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                        <p className="text-sm">
                          <strong className="text-accent">Reframed:</strong> {entry.alternative_thought}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Learn Tab - Enhanced */}
          <TabsContent value="learn" className="space-y-6">
            <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
              <CardHeader>
                <CardTitle>Understanding Cognitive Distortions</CardTitle>
                <CardDescription>
                  Cognitive distortions are habitual ways of thinking that are often inaccurate 
                  and negatively biased. They're not character flaws - they're patterns your brain 
                  developed, often in response to difficult experiences. Learning to identify them 
                  is the first step to changing them.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500 mt-1 shrink-0" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Why they develop:</strong> Usually as protection 
                      mechanisms from past experiences
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-red-500 mt-1 shrink-0" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Their impact:</strong> They filter reality and 
                      can intensify difficult emotions
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Heart className="w-4 h-4 text-green-500 mt-1 shrink-0" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">The good news:</strong> With practice, these 
                      patterns can be reshaped
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              {COGNITIVE_DISTORTIONS.map(distortion => (
                <Collapsible 
                  key={distortion.id}
                  open={expandedDistortion === distortion.id}
                  onOpenChange={() => setExpandedDistortion(
                    expandedDistortion === distortion.id ? null : distortion.id
                  )}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base text-left">{distortion.name}</CardTitle>
                          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${
                            expandedDistortion === distortion.id ? 'rotate-180' : ''
                          }`} />
                        </div>
                        <CardDescription className="text-left">
                          {distortion.description}
                        </CardDescription>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent className="space-y-4 pt-0">
                        <p className="text-sm italic text-muted-foreground/80 p-3 bg-muted/30 rounded-lg">
                          Example: {distortion.example}
                        </p>
                        
                        <div className="grid gap-4">
                          <div className="flex items-start gap-3">
                            <div className="p-1.5 rounded bg-amber-500/10">
                              <Lightbulb className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                                Why It Develops
                              </h5>
                              <p className="text-sm text-muted-foreground mt-1">
                                {distortion.whyItDevelops}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-3">
                            <div className="p-1.5 rounded bg-red-500/10">
                              <Target className="w-4 h-4 text-red-600" />
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-red-700 dark:text-red-400">
                                The Impact
                              </h5>
                              <p className="text-sm text-muted-foreground mt-1">
                                {distortion.impact}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-3">
                            <div className="p-1.5 rounded bg-green-500/10">
                              <Heart className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-green-700 dark:text-green-400">
                                How to Challenge It
                              </h5>
                              <p className="text-sm text-muted-foreground mt-1">
                                {distortion.changeStrategy}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-2 border-t">
                          <p className="text-sm text-primary flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            <span className="font-medium">Quick Challenge:</span> {distortion.challenge}
                          </p>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Quick Links */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Card 
            className="cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/mental-wellness')}
          >
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-2 rounded-full bg-primary/10">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold">Mental Wellness Hub</h4>
                <p className="text-sm text-muted-foreground">Explore all mental wellness tools</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/therapy', { state: { mode: 'cbt' } })}
          >
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-2 rounded-full bg-accent/10">
                <MessageCircle className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h4 className="font-semibold">Therappi CBT Mode</h4>
                <p className="text-sm text-muted-foreground">Get AI support for challenging thoughts</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
